import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faChartBar, 
  faCheckCircle, 
  faExclamationTriangle, 
  faDatabase, 
  faRobot, 
  faTimes,
  faCalendarAlt,
  faFilter,
  faRefresh,
  faDownload,
  faUsers,
  faComments,
  faInfoCircle,
  faTools,
  faCog
} from '@fortawesome/free-solid-svg-icons';
import { adminAPI, toolboxAPI } from '../../services/api';
import { Skeleton } from '../Skeleton';
import './AdminStatistics.css';

interface Statistics {
  rag_count: number;
  basic_count: number;
  not_selected_count: number;
  total_conversations: number;
}

interface DetailedStatistics extends Statistics {
  period_start?: string;
  period_end?: string;
  patients_with_conversations?: number;
  avg_conversations_per_patient?: number;
}

interface ToolboxStatistics {
  total_usage: number;
  chat_usage: number;
  clinical_improvement_usage: number;
  unique_users: number;
  avg_usage_per_user: number;
  usage_by_role: {
    doctor: number;
    admin: number;
  };
}

export const AdminStatistics: React.FC = () => {
  const [statistics, setStatistics] = useState<DetailedStatistics | null>(null);
  const [toolboxStatistics, setToolboxStatistics] = useState<ToolboxStatistics | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [dateRange, setDateRange] = useState({
    startDate: '',
    endDate: ''
  });
  const [isFiltering, setIsFiltering] = useState(false);

  useEffect(() => {
    loadStatistics();
  }, []);

  const loadStatistics = async (startDate?: string, endDate?: string) => {
    setLoading(true);
    setError('');
    try {
      const [chatResponse, toolboxResponse] = await Promise.all([
        adminAPI.getAnswerStatistics(startDate, endDate),
        toolboxAPI.getStatistics(30) // Last 30 days for toolbox
      ]);
      
      setStatistics({
        ...chatResponse,
        period_start: startDate,
        period_end: endDate
      });
      
      setToolboxStatistics(toolboxResponse);
    } catch (error) {
      console.error('Error fetching statistics:', error);
      setError('Błąd podczas ładowania statystyk. Spróbuj ponownie.');
    } finally {
      setLoading(false);
    }
  };

  const handleDateRangeChange = (field: 'startDate' | 'endDate', value: string) => {
    setDateRange((prev) => ({ ...prev, [field]: value }));
  };

  const applyDateFilter = async () => {
    if (!dateRange.startDate && !dateRange.endDate) {
      return;
    }
    
    setIsFiltering(true);
    try {
      await loadStatistics(dateRange.startDate, dateRange.endDate);
    } finally {
      setIsFiltering(false);
    }
  };

  const clearDateFilter = async () => {
    setDateRange({ startDate: '', endDate: '' });
    await loadStatistics();
  };

  const refreshData = async () => {
    if (dateRange.startDate || dateRange.endDate) {
      await loadStatistics(dateRange.startDate, dateRange.endDate);
    } else {
      await loadStatistics();
    }
  };

  const exportStatistics = () => {
    if (!statistics) return;
    
    const exportData = {
      chat_statistics: statistics,
      toolbox_statistics: toolboxStatistics,
      export_date: new Date().toISOString(),
      date_range: {
        start: dateRange.startDate || 'wszystkie',
        end: dateRange.endDate || 'wszystkie'
      },
      summary: {
        total_responses: statistics.rag_count + statistics.basic_count,
        response_rate: getPercentage(statistics.rag_count + statistics.basic_count, statistics.total_conversations),
        rag_preference: getPercentage(statistics.rag_count, statistics.rag_count + statistics.basic_count),
        basic_preference: getPercentage(statistics.basic_count, statistics.rag_count + statistics.basic_count),
        toolbox_summary: toolboxStatistics ? {
          total_toolbox_usage: toolboxStatistics.total_usage,
          chat_vs_improvement: {
            chat_percentage: getPercentage(toolboxStatistics.chat_usage, toolboxStatistics.total_usage),
            improvement_percentage: getPercentage(toolboxStatistics.clinical_improvement_usage, toolboxStatistics.total_usage)
          },
          usage_by_role: toolboxStatistics.usage_by_role || { doctor: 0, admin: 0 }
        } : null
      }
    };
    
    const blob = new Blob([JSON.stringify(exportData, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `statistics_${new Date().toISOString().split('T')[0]}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const getPercentage = (count: number, total: number): string => {
    return total > 0 ? ((count / total) * 100).toFixed(1) : '0';
  };

  const getPercentageNumber = (count: number, total: number): number => {
    return total > 0 ? (count / total) * 100 : 0;
  };

  return (
    <div className="admin-statistics">
      <div className="statistics-header">
        <h1><FontAwesomeIcon icon={faChartBar} /> Statystyki systemu</h1>
        <p>Analiza preferencji pacjentów, efektywności systemu RAG i wykorzystania narzędzi AI</p>
        <div className="header-actions">
          <button 
            onClick={refreshData} 
            className="refresh-button"
            disabled={loading}
            title="Odśwież dane"
          >
            <FontAwesomeIcon icon={faRefresh} className="button-icon" />
            Odśwież
          </button>
          {statistics && (
            <button 
              onClick={exportStatistics} 
              className="export-button"
              title="Eksportuj statystyki"
            >
              <FontAwesomeIcon icon={faDownload} className="button-icon" />
              Eksportuj
            </button>
          )}
        </div>
      </div>

      {/* Date Filter Section */}
      <div className="date-filter card">
        <h3><FontAwesomeIcon icon={faFilter} /> Filtruj według okresu</h3>
        <div className="date-inputs">
          <div className="date-group">
            <label htmlFor="start-date">
              <FontAwesomeIcon icon={faCalendarAlt} /> Data początkowa:
            </label>
            <input
              id="start-date"
              type="date"
              value={dateRange.startDate}
              onChange={(e) => handleDateRangeChange('startDate', e.target.value)}
              disabled={loading || isFiltering}
            />
          </div>
          <div className="date-group">
            <label htmlFor="end-date">
              <FontAwesomeIcon icon={faCalendarAlt} /> Data końcowa:
            </label>
            <input
              id="end-date"
              type="date"
              value={dateRange.endDate}
              onChange={(e) => handleDateRangeChange('endDate', e.target.value)}
              disabled={loading || isFiltering}
            />
          </div>
          <div className="date-buttons">
            <button 
              onClick={applyDateFilter} 
              className="apply-button"
              disabled={loading || isFiltering || (!dateRange.startDate && !dateRange.endDate)}
            >
              <FontAwesomeIcon icon={faFilter} className="button-icon" />
              {isFiltering ? 'Filtrowanie...' : 'Zastosuj filtr'}
            </button>
            <button 
              onClick={clearDateFilter} 
              className="clear-button"
              disabled={loading || isFiltering}
            >
              <FontAwesomeIcon icon={faTimes} className="button-icon" />
              Wyczyść
            </button>
          </div>
        </div>
        {(dateRange.startDate || dateRange.endDate) && (
          <div className="active-filter-info">
            <FontAwesomeIcon icon={faInfoCircle} />
            Aktywny filtr: {dateRange.startDate || 'początek'} - {dateRange.endDate || 'koniec'}
          </div>
        )}
      </div>

      {/* Error Display */}
      {error && (
        <div className="error-message card">
          <FontAwesomeIcon icon={faExclamationTriangle} /> {error}
        </div>
      )}

      {/* Loading State */}
      {loading ? (
        <div className="statistics-content">
          <div className="stats-overview">
            {[...Array(4)].map((_, i) => (
              <div className="stat-card" key={i}>
                <Skeleton height="80px" />
              </div>
            ))}
          </div>
          <div className="charts-section">
            <div className="chart-container card">
              <Skeleton height="300px" />
            </div>
          </div>
        </div>
      ) : statistics ? (
        <div className="statistics-content">
          {/* Statistics Overview */}
          <div className="stats-overview">
            <div className="stat-card total">
              <div className="stat-header">
                <FontAwesomeIcon icon={faComments} className="stat-icon" />
                <h3>Łączna liczba rozmów</h3>
              </div>
              <div className="stat-number">{statistics.total_conversations}</div>
              <div className="stat-description">Wszystkie przeprowadzone rozmowy</div>
            </div>
            
            <div className="stat-card rag">
              <div className="stat-header">
                <FontAwesomeIcon icon={faDatabase} className="stat-icon" />
                <h3>Odpowiedzi z bazą wiedzy</h3>
              </div>
              <div className="stat-number">{statistics.rag_count}</div>
              <div className="stat-percentage">
                {getPercentage(statistics.rag_count, statistics.total_conversations)}% wszystkich rozmów
              </div>
              <div className="stat-description">
                {statistics.rag_count + statistics.basic_count > 0 && (
                  <span className="choice-percentage">
                    {getPercentage(statistics.rag_count, statistics.rag_count + statistics.basic_count)}% wyborów
                  </span>
                )}
              </div>
            </div>
            
            <div className="stat-card basic">
              <div className="stat-header">
                <FontAwesomeIcon icon={faRobot} className="stat-icon" />
                <h3>Odpowiedzi podstawowe</h3>
              </div>
              <div className="stat-number">{statistics.basic_count}</div>
              <div className="stat-percentage">
                {getPercentage(statistics.basic_count, statistics.total_conversations)}% wszystkich rozmów
              </div>
              <div className="stat-description">
                {statistics.rag_count + statistics.basic_count > 0 && (
                  <span className="choice-percentage">
                    {getPercentage(statistics.basic_count, statistics.rag_count + statistics.basic_count)}% wyborów
                  </span>
                )}
              </div>
            </div>
            
            <div className="stat-card not-selected">
              <div className="stat-header">
                <FontAwesomeIcon icon={faTimes} className="stat-icon" />
                <h3>Bez wyboru odpowiedzi</h3>
              </div>
              <div className="stat-number">{statistics.not_selected_count}</div>
              <div className="stat-percentage">
                {getPercentage(statistics.not_selected_count, statistics.total_conversations)}% wszystkich rozmów
              </div>
              <div className="stat-description">Pacjenci nie dokonali wyboru</div>
            </div>
          </div>

          {/* Charts and Analysis */}
          <div className="charts-section">
            <div className="chart-container card">
              <h3><FontAwesomeIcon icon={faChartBar} /> Wizualizacja preferencji</h3>
              <div className="chart-content">
                <div className="chart-bars">
                  <div className="chart-bar">
                    <div className="bar-info">
                      <span className="bar-label">
                        <FontAwesomeIcon icon={faDatabase} /> Z bazą wiedzy
                      </span>
                      <span className="bar-value">{statistics.rag_count}</span>
                    </div>
                    <div className="bar">
                      <div 
                        className="bar-fill rag"
                        style={{ 
                          width: `${Math.max(getPercentageNumber(statistics.rag_count, statistics.total_conversations), 2)}%` 
                        }}
                      ></div>
                    </div>
                    <span className="bar-percentage">
                      {getPercentage(statistics.rag_count, statistics.total_conversations)}%
                    </span>
                  </div>
                  
                  <div className="chart-bar">
                    <div className="bar-info">
                      <span className="bar-label">
                        <FontAwesomeIcon icon={faRobot} /> Podstawowe
                      </span>
                      <span className="bar-value">{statistics.basic_count}</span>
                    </div>
                    <div className="bar">
                      <div 
                        className="bar-fill basic"
                        style={{ 
                          width: `${Math.max(getPercentageNumber(statistics.basic_count, statistics.total_conversations), 2)}%` 
                        }}
                      ></div>
                    </div>
                    <span className="bar-percentage">
                      {getPercentage(statistics.basic_count, statistics.total_conversations)}%
                    </span>
                  </div>
                  
                  <div className="chart-bar">
                    <div className="bar-info">
                      <span className="bar-label">
                        <FontAwesomeIcon icon={faTimes} /> Bez wyboru
                      </span>
                      <span className="bar-value">{statistics.not_selected_count}</span>
                    </div>
                    <div className="bar">
                      <div 
                        className="bar-fill not-selected"
                        style={{ 
                          width: `${Math.max(getPercentageNumber(statistics.not_selected_count, statistics.total_conversations), 2)}%` 
                        }}
                      ></div>
                    </div>
                    <span className="bar-percentage">
                      {getPercentage(statistics.not_selected_count, statistics.total_conversations)}%
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* Toolbox Statistics Section */}
            {toolboxStatistics && toolboxStatistics.usage_by_role && (
              <div className="toolbox-statistics-section">
                <h2><FontAwesomeIcon icon={faTools} /> Statystyki Toolbox (ostatnie 30 dni)</h2>
                
                {/* Toolbox Overview */}
                <div className="stats-overview">
                  <div className="stat-card toolbox-total">
                    <div className="stat-header">
                      <FontAwesomeIcon icon={faTools} className="stat-icon" />
                      <h3>Łączne użycie Toolbox</h3>
                    </div>
                    <div className="stat-number">{toolboxStatistics.total_usage}</div>
                    <div className="stat-description">Wszystkie sesje AI tools</div>
                  </div>
                  
                  <div className="stat-card toolbox-chat">
                    <div className="stat-header">
                      <FontAwesomeIcon icon={faComments} className="stat-icon" />
                      <h3>AI Chat z kontekstem</h3>
                    </div>
                    <div className="stat-number">{toolboxStatistics.chat_usage}</div>
                    <div className="stat-percentage">
                      {getPercentage(toolboxStatistics.chat_usage, toolboxStatistics.total_usage)}% użycia
                    </div>
                    <div className="stat-description">Sesje czatu AI z kontekstem klinicznym</div>
                  </div>
                  
                  <div className="stat-card toolbox-improvement">
                    <div className="stat-header">
                      <FontAwesomeIcon icon={faCog} className="stat-icon" />
                      <h3>Ulepszanie kontekstu</h3>
                    </div>
                    <div className="stat-number">{toolboxStatistics.clinical_improvement_usage}</div>
                    <div className="stat-percentage">
                      {getPercentage(toolboxStatistics.clinical_improvement_usage, toolboxStatistics.total_usage)}% użycia
                    </div>
                    <div className="stat-description">Testowanie i ulepszanie kontekstu klinicznego</div>
                  </div>
                  
                  <div className="stat-card toolbox-users">
                    <div className="stat-header">
                      <FontAwesomeIcon icon={faUsers} className="stat-icon" />
                      <h3>Aktywni użytkownicy</h3>
                    </div>
                    <div className="stat-number">{toolboxStatistics.unique_users}</div>
                    <div className="stat-percentage">
                      Średnio {toolboxStatistics.avg_usage_per_user ? toolboxStatistics.avg_usage_per_user.toFixed(1) : '0.0'} sesji/użytkownik
                    </div>
                    <div className="stat-description">Unikalnych użytkowników narzędzi AI</div>
                  </div>
                </div>

                {/* Toolbox Usage by Role */}
                <div className="chart-container card">
                  <h3><FontAwesomeIcon icon={faUsers} /> Użycie według ról</h3>
                  <div className="chart-content">
                    <div className="chart-bars">
                      <div className="chart-bar">
                        <div className="bar-info">
                          <span className="bar-label">
                            <FontAwesomeIcon icon={faUsers} /> Lekarze
                          </span>
                          <span className="bar-value">{toolboxStatistics.usage_by_role?.doctor || 0}</span>
                        </div>
                        <div className="bar">
                          <div 
                            className="bar-fill doctor"
                            style={{ 
                              width: `${Math.max(getPercentageNumber(toolboxStatistics.usage_by_role?.doctor || 0, toolboxStatistics.total_usage), 2)}%` 
                            }}
                          ></div>
                        </div>
                        <span className="bar-percentage">
                          {getPercentage(toolboxStatistics.usage_by_role?.doctor || 0, toolboxStatistics.total_usage)}%
                        </span>
                      </div>
                      
                      <div className="chart-bar">
                        <div className="bar-info">
                          <span className="bar-label">
                            <FontAwesomeIcon icon={faCog} /> Administratorzy
                          </span>
                          <span className="bar-value">{toolboxStatistics.usage_by_role?.admin || 0}</span>
                        </div>
                        <div className="bar">
                          <div 
                            className="bar-fill admin"
                            style={{ 
                              width: `${Math.max(getPercentageNumber(toolboxStatistics.usage_by_role?.admin || 0, toolboxStatistics.total_usage), 2)}%` 
                            }}
                          ></div>
                        </div>
                        <span className="bar-percentage">
                          {getPercentage(toolboxStatistics.usage_by_role?.admin || 0, toolboxStatistics.total_usage)}%
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Toolbox Insights */}
                <div className="insights card">
                  <h3><FontAwesomeIcon icon={faInfoCircle} /> Wnioski dotyczące Toolbox</h3>
                  <div className="insight-cards">
                    {toolboxStatistics.total_usage === 0 ? (
                      <div className="insight-card neutral">
                        <h4><FontAwesomeIcon icon={faInfoCircle} /> Narzędzia AI nie są używane</h4>
                        <p>Toolbox nie jest jeszcze wykorzystywany przez lekarzy i administratorów. Rozważ szkolenie zespołu.</p>
                      </div>
                    ) : (
                      <>
                        {/* Usage Analysis */}
                        {toolboxStatistics.chat_usage > toolboxStatistics.clinical_improvement_usage ? (
                          <div className="insight-card positive">
                            <h4><FontAwesomeIcon icon={faCheckCircle} /> Aktywne korzystanie z AI Chat</h4>
                            <p>
                              Lekarze i administratorzy najczęściej używają AI Chat z kontekstem klinicznym 
                              ({getPercentage(toolboxStatistics.chat_usage, toolboxStatistics.total_usage)}% wszystkich sesji).
                            </p>
                            <div className="recommendation">
                              <strong>Rekomendacja:</strong> Rozważ dodanie dodatkowych funkcji do AI Chat lub promocję narzędzia do ulepszania kontekstu.
                            </div>
                          </div>
                        ) : toolboxStatistics.clinical_improvement_usage > toolboxStatistics.chat_usage ? (
                          <div className="insight-card positive">
                            <h4><FontAwesomeIcon icon={faCheckCircle} /> Aktywne ulepszanie kontekstu</h4>
                            <p>
                              Zespół medyczny koncentruje się na testowaniu i ulepszaniu kontekstu klinicznego 
                              ({getPercentage(toolboxStatistics.clinical_improvement_usage, toolboxStatistics.total_usage)}% wszystkich sesji).
                            </p>
                            <div className="recommendation">
                              <strong>Rekomendacja:</strong> To świetnie! Kontynuuj monitorowanie jakości ulepszonych kontekstów.
                            </div>
                          </div>
                        ) : (
                          <div className="insight-card neutral">
                            <h4><FontAwesomeIcon icon={faChartBar} /> Zrównoważone wykorzystanie</h4>
                            <p>Oba narzędzia AI są wykorzystywane w podobnym stopniu.</p>
                          </div>
                        )}

                        {/* User Engagement Analysis */}
                        {toolboxStatistics.avg_usage_per_user >= 5 ? (
                          <div className="insight-card positive">
                            <h4><FontAwesomeIcon icon={faCheckCircle} /> Wysokie zaangażowanie</h4>
                            <p>
                              Średnio {toolboxStatistics.avg_usage_per_user.toFixed(1)} sesji na użytkownika wskazuje na wysokie zaangażowanie 
                              i użyteczność narzędzi AI.
                            </p>
                          </div>
                        ) : toolboxStatistics.avg_usage_per_user >= 2 ? (
                          <div className="insight-card neutral">
                            <h4><FontAwesomeIcon icon={faInfoCircle} /> Umiarkowane zaangażowanie</h4>
                            <p>
                              Średnio {toolboxStatistics.avg_usage_per_user.toFixed(1)} sesji na użytkownika. 
                              Narzędzia są używane, ale można zwiększyć ich adopcję.
                            </p>
                            <div className="recommendation">
                              <strong>Rekomendacja:</strong> Rozważ dodatkowe szkolenia lub promocję korzyści z narzędzi AI.
                            </div>
                          </div>
                        ) : (
                          <div className="insight-card warning">
                            <h4><FontAwesomeIcon icon={faExclamationTriangle} /> Niskie zaangażowanie</h4>
                            <p>
                              Średnio {toolboxStatistics.avg_usage_per_user.toFixed(1)} sesji na użytkownika wskazuje na niskie wykorzystanie 
                              narzędzi AI.
                            </p>
                            <div className="recommendation">
                              <strong>Rekomendacja:</strong> Przeprowadź ankietę wśród użytkowników i zidentyfikuj bariery w korzystaniu z narzędzi.
                            </div>
                          </div>
                        )}

                        {/* Role-based Analysis */}
                        {(toolboxStatistics.usage_by_role?.doctor || 0) > (toolboxStatistics.usage_by_role?.admin || 0) * 2 ? (
                          <div className="insight-card info">
                            <h4><FontAwesomeIcon icon={faUsers} /> Dominacja lekarzy</h4>
                            <p>
                              Lekarze używają narzędzi AI znacznie częściej niż administratorzy 
                              ({getPercentage(toolboxStatistics.usage_by_role?.doctor || 0, toolboxStatistics.total_usage)}% vs {getPercentage(toolboxStatistics.usage_by_role?.admin || 0, toolboxStatistics.total_usage)}%).
                            </p>
                          </div>
                        ) : (toolboxStatistics.usage_by_role?.admin || 0) > (toolboxStatistics.usage_by_role?.doctor || 0) * 2 ? (
                          <div className="insight-card info">
                            <h4><FontAwesomeIcon icon={faCog} /> Dominacja administratorów</h4>
                            <p>
                              Administratorzy używają narzędzi AI częściej niż lekarze 
                              ({getPercentage(toolboxStatistics.usage_by_role?.admin || 0, toolboxStatistics.total_usage)}% vs {getPercentage(toolboxStatistics.usage_by_role?.doctor || 0, toolboxStatistics.total_usage)}%).
                            </p>
                          </div>
                        ) : null}
                      </>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Key Metrics */}
            <div className="metrics-container card">
              <h3><FontAwesomeIcon icon={faInfoCircle} /> Kluczowe wskaźniki</h3>
              <div className="metrics-grid">
                <div className="metric-item">
                  <div className="metric-label">Wskaźnik odpowiedzi</div>
                  <div className="metric-value">
                    {getPercentage(statistics.rag_count + statistics.basic_count, statistics.total_conversations)}%
                  </div>
                  <div className="metric-description">
                    Procent rozmów z wybraną odpowiedzią
                  </div>
                </div>
                
                {statistics.rag_count + statistics.basic_count > 0 && (
                  <>
                    <div className="metric-item">
                      <div className="metric-label">Preferencja RAG</div>
                      <div className="metric-value rag-color">
                        {getPercentage(statistics.rag_count, statistics.rag_count + statistics.basic_count)}%
                      </div>
                      <div className="metric-description">
                        Spośród dokonanych wyborów
                      </div>
                    </div>
                    
                    <div className="metric-item">
                      <div className="metric-label">Preferencja podstawowa</div>
                      <div className="metric-value basic-color">
                        {getPercentage(statistics.basic_count, statistics.rag_count + statistics.basic_count)}%
                      </div>
                      <div className="metric-description">
                        Spośród dokonanych wyborów
                      </div>
                    </div>
                  </>
                )}
                
                <div className="metric-item">
                  <div className="metric-label">Współczynnik braku wyboru</div>
                  <div className="metric-value not-selected-color">
                    {getPercentage(statistics.not_selected_count, statistics.total_conversations)}%
                  </div>
                  <div className="metric-description">
                    Wymaga optymalizacji UX
                  </div>
                </div>
              </div>
            </div>

            {/* Insights and Recommendations */}
            <div className="insights card">
              <h3><FontAwesomeIcon icon={faCheckCircle} /> Wnioski i rekomendacje</h3>
              <div className="insight-cards">
                {statistics.total_conversations === 0 ? (
                  <div className="insight-card neutral">
                    <h4><FontAwesomeIcon icon={faInfoCircle} /> Brak danych</h4>
                    <p>Nie ma jeszcze wystarczających danych do analizy. Poczekaj na więcej rozmów pacjentów.</p>
                  </div>
                ) : (
                  <>
                    {/* RAG vs Basic Analysis */}
                    {statistics.rag_count > statistics.basic_count ? (
                      <div className="insight-card positive">
                        <h4><FontAwesomeIcon icon={faCheckCircle} /> Sukces systemu RAG</h4>
                        <p>
                          Pacjenci preferują odpowiedzi wzbogacone bazą wiedzy 
                          ({getPercentage(statistics.rag_count, statistics.rag_count + statistics.basic_count)}% wyborów). 
                          System RAG skutecznie dostarcza wartościowe informacje.
                        </p>
                        <div className="recommendation">
                          <strong>Rekomendacja:</strong> Kontynuuj rozwój bazy wiedzy i monitoruj jakość odpowiedzi RAG.
                        </div>
                      </div>
                    ) : statistics.basic_count > statistics.rag_count ? (
                      <div className="insight-card warning">
                        <h4><FontAwesomeIcon icon={faExclamationTriangle} /> Analiza wymagana</h4>
                        <p>
                          Pacjenci częściej wybierają odpowiedzi podstawowe 
                          ({getPercentage(statistics.basic_count, statistics.rag_count + statistics.basic_count)}% wyborów). 
                        </p>
                        <div className="recommendation">
                          <strong>Rekomendacja:</strong> Przeanalizuj jakość dokumentów w bazie wiedzy i sposób prezentacji odpowiedzi RAG.
                        </div>
                      </div>
                    ) : statistics.rag_count + statistics.basic_count > 0 ? (
                      <div className="insight-card neutral">
                        <h4><FontAwesomeIcon icon={faChartBar} /> Równowaga preferencji</h4>
                        <p>
                          Pacjenci wybierają oba typy odpowiedzi w podobnych proporcjach. 
                          To może wskazywać na różnorodność potrzeb informacyjnych.
                        </p>
                      </div>
                    ) : null}

                    {/* Response Rate Analysis */}
                    {statistics.not_selected_count / statistics.total_conversations > 0.4 ? (
                      <div className="insight-card error">
                        <h4><FontAwesomeIcon icon={faExclamationTriangle} /> Krytyczny poziom braku wyboru</h4>
                        <p>
                          {getPercentage(statistics.not_selected_count, statistics.total_conversations)}% 
                          pacjentów nie wybiera żadnej odpowiedzi. To wskazuje na poważne problemy z interfejsem.
                        </p>
                        <div className="recommendation">
                          <strong>Pilne działania:</strong> Uprość interfejs wyboru, dodaj wskazówki wizualne i rozważ automatyczny wybor po czasie.
                        </div>
                      </div>
                    ) : statistics.not_selected_count / statistics.total_conversations > 0.25 ? (
                      <div className="insight-card warning">
                        <h4><FontAwesomeIcon icon={faExclamationTriangle} /> Wysoki odsetek braku wyboru</h4>
                        <p>
                          {getPercentage(statistics.not_selected_count, statistics.total_conversations)}% 
                          pacjentów nie wybiera odpowiedzi.
                        </p>
                        <div className="recommendation">
                          <strong>Rekomendacja:</strong> Dodaj przypomnienia o wyborze odpowiedzi i popraw user experience.
                        </div>
                      </div>
                    ) : (
                      <div className="insight-card positive">
                        <h4><FontAwesomeIcon icon={faCheckCircle} /> Dobry wskaźnik odpowiedzi</h4>
                        <p>
                          {getPercentage(statistics.rag_count + statistics.basic_count, statistics.total_conversations)}% 
                          pacjentów dokonuje wyboru odpowiedzi. To świadczy o dobrym designie interfejsu.
                        </p>
                      </div>
                    )}

                    {/* Sample Size Analysis */}
                    {statistics.total_conversations < 50 && (
                      <div className="insight-card info">
                        <h4><FontAwesomeIcon icon={faInfoCircle} /> Mała próba danych</h4>
                        <p>
                          Mała liczba rozmów ({statistics.total_conversations}) może wpływać na wiarygodność analiz. 
                          Statystyki staną się bardziej miarodajne wraz ze wzrostem liczby rozmów.
                        </p>
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      ) : (
        <div className="no-data card">
          <FontAwesomeIcon icon={faChartBar} className="no-data-icon" />
          <h3>Brak danych do wyświetlenia</h3>
          <p>Nie znaleziono żadnych statystyk dla wybranego okresu.</p>
          <button onClick={clearDateFilter} className="retry-button">
            <FontAwesomeIcon icon={faRefresh} /> Pokaż wszystkie dane
          </button>
        </div>
      )}
    </div>
  );
};
